import { GridColDef } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { addItemsBaseColumnProps } from './useAddItemsBaseColumns';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import { AddCampaignItemsSchema, BaseCampaignItemSchema } from '..';
import { useFormContext } from 'react-hook-form';
import { PriceSuggestionType } from '@retail/products/types';
import { formatPrice } from '@retail/products/utils';
import { useTranslation } from 'react-i18next';
import { PricePerUnit } from '@retail/products/components';
import { BaseMgSupplierCampaignItem } from '@retail/campaign/types';
import { calculateVatPrice } from '@retail/utils';
import { StackedTextCell } from '@retail/components';

const getPriceSuggestionPriceForUnit = (
  item: BaseMgSupplierCampaignItem,
  unit: string,
  priceSuggestionType: PriceSuggestionType,
  incVat: boolean
): number | null => {
  const finfoRecPrice = item.priceSuggestions
    .filter(({ channel }) => channel === 'SE_RETAIL_BS')
    .find((priceSuggestion) => priceSuggestion.priceSuggestionType === priceSuggestionType);
  if (!finfoRecPrice) return null;

  const packageForUnit = item.packages.find((p) => p.unit.unit === unit);

  if (!packageForUnit) return calculateVatPrice(finfoRecPrice.price, incVat);

  return calculateVatPrice(finfoRecPrice.price, incVat) * packageForUnit.calculatedCount;
};

export const usePriceSuggestionColumns = <T extends BaseMgSupplierCampaignItem>() => {
  const t = useCampaignTFunction();
  const { i18n } = useTranslation();

  const { watch } = useFormContext<AddCampaignItemsSchema<BaseCampaignItemSchema>>();

  return useMemo(() => {
    const getFinfoRecPriceColumn = (incVat: boolean): GridColDef<T> => ({
      ...addItemsBaseColumnProps,
      width: 200,
      headerAlign: 'center',
      align: 'center',
      field: 'finfoRecPrice',
      headerName: t('campaign.columns.finfoRecPrice'),
      renderCell: ({ row }) => {
        const campaignUnit = watch('items')[row.mgSupplierItemId]?.campaignUnit;
        const price = getPriceSuggestionPriceForUnit(
          row,
          campaignUnit,
          'FINFO_RECOMMENDED',
          incVat
        );

        return price ? (
          <StackedTextCell
            topLine={<PricePerUnit price={formatPrice(price, i18n.language)} unit={campaignUnit} />}
            bottomLine={incVat ? t('campaign.columns.incVat') : t('campaign.columns.exVat')}
            gap="small"
          />
        ) : (
          '-'
        );
      },
    });

    const getFinfoBasePriceColumn = (incVat: boolean): GridColDef<T> => ({
      ...addItemsBaseColumnProps,
      width: 200,
      headerAlign: 'center',
      align: 'center',
      field: 'finfoBasePrice',
      headerName: t('campaign.columns.finfoBasePrice'),
      renderCell: ({ row }) => {
        const campaignUnit = watch('items')[row.mgSupplierItemId]?.campaignUnit;
        const price = getPriceSuggestionPriceForUnit(row, campaignUnit, 'FINFO_BASE', incVat);

        return price ? (
          <StackedTextCell
            topLine={<PricePerUnit price={formatPrice(price, i18n.language)} unit={campaignUnit} />}
            bottomLine={incVat ? t('campaign.columns.incVat') : t('campaign.columns.exVat')}
            gap="small"
          />
        ) : (
          '-'
        );
      },
    });

    return { getFinfoRecPriceColumn, getFinfoBasePriceColumn };
  }, [i18n.language, t, watch]);
};
